"use client"

import { useEffect, useRef, useState, useCallback } from "react"
import {
  Box, RotateCcw, AlertCircle, Eye, EyeOff,
  Minus, Plus, SlidersHorizontal, ChevronDown,
} from "lucide-react"
import { useTheme } from "next-themes"
import type * as ThreeNS from "three"

// Theme palettes for the 3D scene
const THEME_PALETTE = {
  dark: {
    bg:        0x0d1117,
    ambient:   0.55,
    fillColor: 0x8aaeff,
    fillStr:   0.35,
    plate:     0x3a4150,
    plateSpec: 0x222831,
    bolt:      0x1a1d24,
  },
  light: {
    bg:        0xeef2f6,
    ambient:   0.85,
    fillColor: 0xffd6a8,
    fillStr:   0.25,
    plate:     0x94a3b8,
    plateSpec: 0x6b7280,
    bolt:      0x475569,
  },
} as const

interface MeshData {
  name: string
  color: { r: number; g: number; b: number } | null
  index: number[]
  position: number[]
  normal: number[] | null
}

type Status = "loading" | "ready" | "error"

// ============================================================================
// PART COLOR VARIABLES — KR10 R1100 sixx
//
// One variable per named physical part. To change the color of a specific
// part, edit ONLY that variable. `pickColor()` below routes each STEP mesh to
// the correct variable using bounding-box heuristics (mesh names are missing
// in the STEP export).
//
// Right-hand comment for each variable lists:
//   - which STEP mesh index(es) this color is applied to, and
//   - where on the real robot you see this part.
// ============================================================================

// ── Mounting hardware (Link 0, stationary) ─────────────────────────────────
const COLOR_MOUNTING_PLATE      = { r: 0.05,  g: 0.05,  b: 0.06  } // STEP mesh 11 — flat ceiling/floor plate the robot bolts to.
const COLOR_MOUNTING_BOLTS      = { r: 0.10,  g: 0.11,  b: 0.14  } // Procedural — the 4 corner bolts we draw next to the plate (not in STEP).

// ── Base column (Link 0, stationary) ───────────────────────────────────────
const COLOR_BASE_COLUMN         = { r: 0.815, g: 0.345, b: 0.012 } // STEP mesh 0 — large casting under the body that houses the A1 motor (deep "Link 1" orange band).

// ── Joint covers (round black side-caps) ───────────────────────────────────
const COLOR_JOINT_CAP_A2        = { r: 0.078, g: 0.078, b: 0.090 } // STEP mesh 1 (source-green tag) — A1/A2 shoulder joint cover.
const COLOR_JOINT_CAP_A4        = { r: 0.078, g: 0.078, b: 0.090 } // STEP mesh 2 (source-green tag) — A4 wrist joint cover.

// ── Body parts (orange, Z-banded so adjacent links read differently) ──────
const COLOR_SHOULDER_LINK1      = { r: 0.933, g: 0.412, b: 0.020 } // STEP meshes with 250 ≤ cz < 700 (e.g. mesh 9 around shoulder transition) — Link 1 between A1 housing and A2.
const COLOR_LOWER_ARM_LINK2     = { r: 0.933, g: 0.412, b: 0.020 } // STEP mesh 9 (cz≈680, X<200) — the long vertical lower arm between A2 and A3.
const COLOR_FOREARM_LINK3       = { r: 0.965, g: 0.510, b: 0.165 } // STEP mesh 7 (cx≈357, cz≈1011) — horizontal forearm spine between A3 and A5.
const COLOR_WRIST_HOUSING_LINK5 = { r: 0.988, g: 0.604, b: 0.275 } // STEP mesh 8 (cx≈559, cz≈995) — wrist pitch housing around A5 / A6.

// ── Cabling (off-centre small parts riding the side of the arm) ───────────
const COLOR_CABLE_CARRIER       = { r: 0.30,  g: 0.31,  b: 0.34  } // STEP meshes 12-33 (|cy|>90, small) — cable hose / cable tray.

// ── Tool flange (very end of the wrist) ────────────────────────────────────
const COLOR_TOOL_FLANGE         = { r: 0.62,  g: 0.64,  b: 0.68  } // STEP mesh 10 + small details near (cx>560, cz>950) — metallic mounting disk where the EOAT bolts on.
const COLOR_TOOL_FLANGE_ACCENT  = { r: 0.86,  g: 0.18,  b: 0.18  } // STEP meshes 3-6 (very small, cx>590) — small red orientation marker on the flange.


// Source-color tags present in the STEP file
const SRC_YELLOW_R = 1.0
const SRC_YELLOW_G = 0.956

interface BBox {
  minX: number; minY: number; minZ: number
  maxX: number; maxY: number; maxZ: number
  cx: number; cy: number; cz: number
  sx: number; sy: number; sz: number
}

function computeBBox(position: number[]): BBox {
  let minX = Infinity, minY = Infinity, minZ = Infinity
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity
  for (let i = 0; i < position.length; i += 3) {
    const x = position[i], y = position[i + 1], z = position[i + 2]
    if (x < minX) minX = x; if (x > maxX) maxX = x
    if (y < minY) minY = y; if (y > maxY) maxY = y
    if (z < minZ) minZ = z; if (z > maxZ) maxZ = z
  }
  return {
    minX, minY, minZ, maxX, maxY, maxZ,
    cx: (minX + maxX) / 2, cy: (minY + maxY) / 2, cz: (minZ + maxZ) / 2,
    sx: maxX - minX, sy: maxY - minY, sz: maxZ - minZ,
  }
}

// Route each STEP mesh to its per-part color variable.
// Each branch comment names the real-world part the rule targets.
function pickColor(
  src: { r: number; g: number; b: number } | null,
  bb: BBox
): { r: number; g: number; b: number } {
  // Mounting plate (STEP mesh 11) — thin wide disk at ground level
  if (bb.maxZ < 50 && bb.sz < 60 && Math.max(bb.sx, bb.sy) > 200) {
    return COLOR_MOUNTING_PLATE
  }

  // Tool-flange accent disk (STEP meshes 3-6) — very small parts at tool tip
  if (bb.cx > 590 && bb.cz > 970 && Math.max(bb.sx, bb.sy, bb.sz) < 90) {
    return COLOR_TOOL_FLANGE_ACCENT
  }

  // Tool flange body (STEP mesh 10 + small wrist-end details)
  if (bb.cx > 560 && bb.cz > 950 && Math.max(bb.sx, bb.sy, bb.sz) < 140) {
    return COLOR_TOOL_FLANGE
  }

  // Joint covers — STEP meshes tagged bright green in the source file.
  // Two physical caps share this tag: A2 (mesh 1, cz≈351) and A4 (mesh 2, cz≈969).
  if (src && src.r < 0.75 && src.g > 0.85 && src.b < 0.2) {
    return bb.cz < 600 ? COLOR_JOINT_CAP_A2 : COLOR_JOINT_CAP_A4
  }

  // Cable carrier hose — small parts riding along the ±Y side of the arm
  const isYellow =
    src &&
    Math.abs(src.r - SRC_YELLOW_R) < 0.05 &&
    Math.abs(src.g - SRC_YELLOW_G) < 0.05
  const offCenterY = Math.abs(bb.cy) > 90
  const smallish = Math.max(bb.sx, bb.sy, bb.sz) < 250
  if (isYellow && offCenterY && smallish) return COLOR_CABLE_CARRIER

  // Body parts — Z-banded orange variants so adjacent links remain readable
  if (bb.cz < 250) return COLOR_BASE_COLUMN         // STEP mesh 0 — base column casting
  if (bb.cz < 500) return COLOR_SHOULDER_LINK1      // Link 1 region (between A1 and A2)
  if (bb.cz < 700) return COLOR_LOWER_ARM_LINK2     // STEP mesh 9 — vertical lower arm
  if (bb.cz < 950) return COLOR_FOREARM_LINK3       // STEP mesh 7 — horizontal forearm spine
  return COLOR_WRIST_HOUSING_LINK5                  // STEP mesh 8 — wrist body / end housing
}

// Joint definitions in STEP-native coordinates (Z up, mm)
// axis: unit vector of the rotation axis in STEP frame
// axisLetter: which local Euler component to drive ('x' | 'y' | 'z') — derived from `axis`
// min / max: per-joint angle limits in degrees (KR10 R1100 datasheet)
interface JointSpec {
  id: string
  pos: [number, number, number]
  axis: [number, number, number]
  axisLetter: "x" | "y" | "z"
  hue: number
  radius: number
  labelOffset: [number, number, number]
  min: number
  max: number
}

const JOINTS: JointSpec[] = [
  { id: "A1", pos: [0,    0, 230],  axis: [0, 0, 1], axisLetter: "z", hue: 0x00d4ff, radius: 90, labelOffset: [0,  -130,  10], min: -170, max:  170 },
  { id: "A2", pos: [10,   0, 360],  axis: [0, 1, 0], axisLetter: "y", hue: 0x4ade80, radius: 95, labelOffset: [0,   135, -55], min: -90, max:   90 },
  { id: "A3", pos: [90,   0, 985],  axis: [0, 1, 0], axisLetter: "y", hue: 0xfbbf24, radius: 75, labelOffset: [0,   135,  75], min: -200, max:  72 },
  { id: "A4", pos: [320,  0, 990],  axis: [1, 0, 0], axisLetter: "x", hue: 0xec4899, radius: 55, labelOffset: [0,   -85, -60], min: -185, max:  185 },
  { id: "A5", pos: [555,  0, 990],  axis: [0, 1, 0], axisLetter: "y", hue: 0xf97316, radius: 48, labelOffset: [0,    80,  65], min: -120, max:  120 },
  { id: "A6", pos: [625,  0, 1000], axis: [1, 0, 0], axisLetter: "x", hue: 0xef4444, radius: 42, labelOffset: [0,   -65, -55], min: -350, max:  350 },
]

// Mesh → link index (0..6) heuristic. Link 0 is stationary; Link N rotates
// with axes A1..AN.  Cutoffs were tuned against the parsed bbox dump of the
// STEP file (run /api/model and inspect cx, cy, cz, sx, sy, sz per mesh).
function assignLink(bb: BBox): number {
  // Mounting plate disk + base column → Link 0 (stationary)
  if (bb.maxZ < 50 && bb.sz < 60 && Math.max(bb.sx, bb.sy) > 200) return 0
  if (bb.cz < 260) return 0
  // A1/A2 shoulder housing (the green-tagged big cap) → Link 1
  if (bb.cz < 500) return 1
  // Lower arm vertical segment between A2 and A3 → Link 2
  if (bb.cz < 850 && bb.cx < 200) return 2
  // Elbow region + early forearm (rotates with A3) → Link 3
  if (bb.cx < 350) return 3
  // Forearm spine (rotates with A4, the forearm roll) → Link 4
  if (bb.cx < 540) return 4
  // Wrist pitch housing (rotates with A5) → Link 5
  if (bb.cx < 605) return 5
  // Tool flange tip → Link 6
  return 6
}

function makeLabelSprite(
  THREE: typeof ThreeNS,
  text: string,
  hex: number
): ThreeNS.Sprite {
  const canvas = document.createElement("canvas")
  canvas.width = 128
  canvas.height = 64
  const ctx = canvas.getContext("2d")!
  ctx.fillStyle = `#${hex.toString(16).padStart(6, "0")}`
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.fillStyle = "#000000"
  ctx.font = "bold 40px ui-sans-serif, system-ui"
  ctx.textAlign = "center"
  ctx.textBaseline = "middle"
  ctx.fillText(text, canvas.width / 2, canvas.height / 2 + 2)

  const tex = new THREE.CanvasTexture(canvas)
  tex.minFilter = THREE.LinearFilter
  tex.magFilter = THREE.LinearFilter
  const mat = new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true })
  const sprite = new THREE.Sprite(mat)
  sprite.scale.set(120, 60, 1)
  return sprite
}

export function StepModelViewer() {
  const mountRef = useRef<HTMLDivElement>(null)
  const cleanupRef = useRef<(() => void) | null>(null)
  // overlayGroupRef now points at the root `group` — traversed for show/hide
  const overlayGroupRef = useRef<ThreeNS.Group | null>(null)
  // Movable kinematic link groups (linkGroups[1..6]) exposed for angle control
  const jointGroupsRef = useRef<ThreeNS.Group[] | null>(null)
  // Live refs for theme-driven scene mutation (no rebuild needed)
  const sceneRef = useRef<ThreeNS.Scene | null>(null)
  const ambientRef = useRef<ThreeNS.AmbientLight | null>(null)
  const fillLightRef = useRef<ThreeNS.DirectionalLight | null>(null)
  const plateMatRef = useRef<ThreeNS.MeshPhongMaterial | null>(null)
  const boltMatRef = useRef<ThreeNS.MeshPhongMaterial | null>(null)
  const [status, setStatus] = useState<Status>("loading")
  const [errorMsg, setErrorMsg] = useState("")
  const [showAxes, setShowAxes] = useState(true)
  const [showControls, setShowControls] = useState(true)
  // 6 joint angles in degrees, indexed 0..5 (A1..A6)
  const [angles, setAngles] = useState<number[]>([0, 0, 0, 0, 0, 0])

  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme !== "light" // default to dark
  const palette = isDark ? THEME_PALETTE.dark : THEME_PALETTE.light

  const load = useCallback(async () => {
    setStatus("loading")
    setErrorMsg("")

    cleanupRef.current?.()
    cleanupRef.current = null
    overlayGroupRef.current = null
    jointGroupsRef.current = null

    const mount = mountRef.current
    if (!mount) return

    try {
      // 1 — Fetch mesh data
      const res = await fetch("/api/model")
      const data: { meshes?: MeshData[]; error?: string } = await res.json().catch(() => ({
        error: `HTTP ${res.status}`,
      }))
      if (!res.ok || data.error) throw new Error(data.error ?? `HTTP ${res.status}`)
      const meshes = data.meshes!

      // 2 — Lazy-load Three.js
      const THREE = await import("three")
      const { OrbitControls } = await import("three/addons/controls/OrbitControls.js")

      // 3 — Scene (background tracks current theme)
      const scene = new THREE.Scene()
      scene.background = new THREE.Color(palette.bg)
      sceneRef.current = scene

      const w = mount.clientWidth || 400
      const h = mount.clientHeight || 300
      const camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 100000)

      const renderer = new THREE.WebGLRenderer({ antialias: true })
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
      renderer.setSize(w, h)
      mount.appendChild(renderer.domElement)

      // 4 — Controls
      const controls = new OrbitControls(camera, renderer.domElement)
      controls.enableDamping = true
      controls.dampingFactor = 0.07

      // 5 — Lights (theme-driven intensity / fill color)
      const ambient = new THREE.AmbientLight(0xffffff, palette.ambient)
      scene.add(ambient)
      ambientRef.current = ambient

      const sun = new THREE.DirectionalLight(0xffffff, 1.1)
      sun.position.set(3000, 5000, 3000)
      scene.add(sun)

      const fill = new THREE.DirectionalLight(palette.fillColor, palette.fillStr)
      fill.position.set(-3000, -2000, -3000)
      scene.add(fill)
      fillLightRef.current = fill

      // 6 — Build the kinematic chain (nested link groups) + meshes.
      //
      //   group (STEP Z-up → Three Y-up wrapper)
      //   └── linkGroups[0] (stationary base) ───┐ contains base + plate + ring A1
      //       └── linkGroups[1] (joint A1, Z) ───┤ contains A1/A2 housing + ring A2
      //           └── linkGroups[2] (joint A2, Y) ─┤ contains lower arm + ring A3
      //               └── linkGroups[3] (joint A3, Y) ─┤ contains elbow region + ring A4
      //                   └── linkGroups[4] (joint A4, X) ─┤ contains forearm + ring A5
      //                       └── linkGroups[5] (joint A5, Y) ─┤ contains wrist + ring A6
      //                           └── linkGroups[6] (joint A6, X) ─┘ contains tool flange
      //
      // Each linkGroups[N] (N ≥ 1) is positioned at its joint's local offset from
      // the previous joint, so its rotation pivots exactly on the joint. Meshes
      // are added with position = -JOINT_N_POS_WORLD so their STEP-baked geometry
      // still appears at the right place when all angles are 0.
      const group = new THREE.Group()
      const disposables: Array<ThreeNS.BufferGeometry | ThreeNS.Material | ThreeNS.Texture> = []

      const linkGroups: ThreeNS.Group[] = []
      for (let i = 0; i < 7; i++) linkGroups.push(new THREE.Group())

      // Link 0 lives inside `group` at world origin; each subsequent linkGroups[N]
      // is added inside linkGroups[N-1] at the offset between two adjacent joints.
      group.add(linkGroups[0])
      for (let n = 1; n <= 6; n++) {
        const prev = n === 1 ? [0, 0, 0] : JOINTS[n - 2].pos
        const here = JOINTS[n - 1].pos
        linkGroups[n].position.set(here[0] - prev[0], here[1] - prev[1], here[2] - prev[2])
        linkGroups[n - 1].add(linkGroups[n])
      }

      // World-space joint positions (used to offset mesh.position so STEP-baked
      // vertex coords land correctly when the parent group is itself offset).
      const linkOriginWorld: [number, number, number][] = [
        [0, 0, 0],
        ...JOINTS.map((j) => j.pos),
      ]

      for (const m of meshes) {
        const geo = new THREE.BufferGeometry()
        geo.setIndex(new THREE.BufferAttribute(new Uint32Array(m.index), 1))
        geo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(m.position), 3))
        if (m.normal) {
          geo.setAttribute("normal", new THREE.BufferAttribute(new Float32Array(m.normal), 3))
        } else {
          geo.computeVertexNormals()
        }

        const bb = computeBBox(m.position)
        const c = pickColor(m.color, bb)
        // Surface-finish categories — drive shininess / specular tuning below.
        const isBlack  = c === COLOR_JOINT_CAP_A2 || c === COLOR_JOINT_CAP_A4 || c === COLOR_MOUNTING_PLATE
        const isMetal  = c === COLOR_TOOL_FLANGE
        const isCable  = c === COLOR_CABLE_CARRIER
        const isAccent = c === COLOR_TOOL_FLANGE_ACCENT

        const mat = new THREE.MeshPhongMaterial({
          color: new THREE.Color(c.r, c.g, c.b),
          shininess: isMetal ? 150 : isBlack ? 90 : isCable ? 18 : isAccent ? 60 : 42,
          specular: new THREE.Color(
            isMetal ? 0xb8b8b8 : isBlack ? 0x202020 : isCable ? 0x1a1a1a : 0x4a4a4a
          ),
          side: THREE.DoubleSide,
        })

        const linkIdx = assignLink(bb)
        const [ox, oy, oz] = linkOriginWorld[linkIdx]
        const mesh = new THREE.Mesh(geo, mat)
        mesh.position.set(-ox, -oy, -oz)
        linkGroups[linkIdx].add(mesh)
        disposables.push(geo, mat)

        // Add subtle edge highlights to non-trivial body parts so adjacent
        // links remain visually separable even when their orange shades are close.
        if (!isMetal && !isCable && Math.max(bb.sx, bb.sy, bb.sz) > 80) {
          const edgesGeo = new THREE.EdgesGeometry(geo, 32)
          const edgesMat = new THREE.LineBasicMaterial({
            color: isBlack ? 0x555555 : 0x1a0e05,
            transparent: true,
            opacity: isBlack ? 0.35 : 0.28,
          })
          const edges = new THREE.LineSegments(edgesGeo, edgesMat)
          edges.position.set(-ox, -oy, -oz)
          linkGroups[linkIdx].add(edges)
          disposables.push(edgesGeo, edgesMat)
        }
      }

      // 6a — Ceiling mounting plate (added to Link 0 — stationary base).
      // Robot base disk occupies STEP Z = -15 .. +30. After group.rotation.x = +π/2,
      // STEP +Z maps to world -Y, so the robot hangs downward and STEP Z values
      // BELOW -15 appear ABOVE the robot — that's where the plate goes.
      const PLATE_W = 620
      const PLATE_T = 28
      const PLATE_TOP_Z = -15
      const PLATE_BOTTOM_Z = PLATE_TOP_Z - PLATE_T

      const plateGeo = new THREE.BoxGeometry(PLATE_W, PLATE_W, PLATE_T)
      const plateMat = new THREE.MeshPhongMaterial({
        color: palette.plate,
        shininess: 25,
        specular: palette.plateSpec,
      })
      plateMatRef.current = plateMat
      const plate = new THREE.Mesh(plateGeo, plateMat)
      plate.position.set(0, 0, (PLATE_TOP_Z + PLATE_BOTTOM_Z) / 2)
      linkGroups[0].add(plate)
      disposables.push(plateGeo, plateMat)

      const boltGeo = new THREE.CylinderGeometry(11, 11, PLATE_T + 6, 18)
      const boltMat = new THREE.MeshPhongMaterial({
        color: palette.bolt,
        shininess: 90,
        specular: 0x555555,
      })
      boltMatRef.current = boltMat
      const boltInset = 45
      const corners: Array<[number, number]> = [
        [+PLATE_W / 2 - boltInset, +PLATE_W / 2 - boltInset],
        [-PLATE_W / 2 + boltInset, +PLATE_W / 2 - boltInset],
        [+PLATE_W / 2 - boltInset, -PLATE_W / 2 + boltInset],
        [-PLATE_W / 2 + boltInset, -PLATE_W / 2 + boltInset],
      ]
      for (const [bx, by] of corners) {
        const bolt = new THREE.Mesh(boltGeo, boltMat)
        bolt.rotation.x = Math.PI / 2
        bolt.position.set(bx, by, (PLATE_TOP_Z + PLATE_BOTTOM_Z) / 2)
        linkGroups[0].add(bolt)
      }
      disposables.push(boltGeo, boltMat)

      // STEP is Z-up; Three.js is Y-up — keep original orientation
      group.rotation.x = Math.PI / 2
      scene.add(group)

      // Expose the 6 movable joint groups to React for live angle control.
      jointGroupsRef.current = linkGroups.slice(1, 7) as ThreeNS.Group[]
      // overlayGroupRef now points at link 0 — used only as a traversal root
      // for the show/hide effect.
      overlayGroupRef.current = group as unknown as ThreeNS.Group

      // 6b — Rotation-arrow rings for each joint.
      // Ring for joint AN is attached to its PARENT link (linkGroups[N-1])
      // so it stays glued to the rotor-side casing as the chain articulates.
      const DEFAULT_RING_NORMAL = new THREE.Vector3(0, 0, 1)
      const ARC_SPAN = (3 / 2) * Math.PI // 270°

      JOINTS.forEach((j, idx) => {
        const parentLink = idx // ring AN goes into linkGroups[N-1] = linkGroups[idx]
        const [px, py, pz] = linkOriginWorld[parentLink]
        const localPos: [number, number, number] = [j.pos[0] - px, j.pos[1] - py, j.pos[2] - pz]

        const axisDir = new THREE.Vector3(j.axis[0], j.axis[1], j.axis[2]).normalize()
        const alignQuat = new THREE.Quaternion().setFromUnitVectors(DEFAULT_RING_NORMAL, axisDir)
        const tubeRadius = Math.max(2.5, j.radius * 0.06)

        const ringGeo = new THREE.TorusGeometry(j.radius, tubeRadius, 12, 72, ARC_SPAN)
        const ringMat = new THREE.MeshBasicMaterial({
          color: j.hue, transparent: true, opacity: 0.95, depthTest: false,
        })
        const ring = new THREE.Mesh(ringGeo, ringMat)
        ring.position.set(...localPos)
        ring.setRotationFromQuaternion(alignQuat)
        ring.renderOrder = 999
        ring.userData.kind = "axisRing"
        ring.userData.isAxisOverlay = true
        ring.visible = showAxes
        linkGroups[parentLink].add(ring)
        disposables.push(ringGeo, ringMat)

        // Arrowhead at end of arc
        const endAngle = ARC_SPAN
        const localTip = new THREE.Vector3(
          Math.cos(endAngle) * j.radius, Math.sin(endAngle) * j.radius, 0
        )
        const localTangent = new THREE.Vector3(-Math.sin(endAngle), Math.cos(endAngle), 0)
        const coneHeight = j.radius * 0.32
        const coneRadius = j.radius * 0.13
        const coneGeo = new THREE.ConeGeometry(coneRadius, coneHeight, 14)
        const coneMat = new THREE.MeshBasicMaterial({
          color: j.hue, transparent: true, opacity: 0.98, depthTest: false,
        })
        const cone = new THREE.Mesh(coneGeo, coneMat)
        cone.setRotationFromQuaternion(
          new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), localTangent)
        )
        cone.position.copy(localTip).addScaledVector(localTangent, coneHeight / 2)
        cone.renderOrder = 1000
        cone.userData.isAxisOverlay = true
        ring.add(cone) // inherits visibility & spin
        disposables.push(coneGeo, coneMat)

        // Label sprite
        const label = makeLabelSprite(THREE, j.id, j.hue)
        label.position.set(
          localPos[0] + j.labelOffset[0],
          localPos[1] + j.labelOffset[1],
          localPos[2] + j.labelOffset[2]
        )
        label.renderOrder = 1001
        label.userData.isAxisOverlay = true
        label.visible = showAxes
        linkGroups[parentLink].add(label)
        if (label.material.map) disposables.push(label.material.map)
        disposables.push(label.material)
      })

      // Base coordinate triad at world origin (stationary, in Link 0)
      const triad = new THREE.AxesHelper(180)
      ;(triad.material as ThreeNS.Material).depthTest = false
      triad.renderOrder = 997
      triad.userData.isAxisOverlay = true
      triad.visible = showAxes
      linkGroups[0].add(triad)
      disposables.push(triad.geometry, triad.material as ThreeNS.Material)

      // 7 — Fit camera
      const box3 = new THREE.Box3().setFromObject(group)
      const center = box3.getCenter(new THREE.Vector3())
      const size3 = box3.getSize(new THREE.Vector3())
      const maxDim = Math.max(size3.x, size3.y, size3.z)

      camera.position.set(
        center.x + maxDim * 1.4,
        center.y + maxDim * 0.7,
        center.z + maxDim * 1.4
      )
      camera.far = maxDim * 20
      camera.updateProjectionMatrix()
      controls.target.copy(center)
      controls.update()

      setStatus("ready")

      // 9 — Resize observer
      const onResize = () => {
        const nw = mount.clientWidth
        const nh = mount.clientHeight
        camera.aspect = nw / nh
        camera.updateProjectionMatrix()
        renderer.setSize(nw, nh)
      }
      const ro = new ResizeObserver(onResize)
      ro.observe(mount)

      // 10 — Render loop: slowly spin each rotation-arrow ring around its own
      //      local +Z (= the joint axis in world space, post alignment quat).
      let raf: number
      let lastT = performance.now()
      const SPIN_SPEED = 0.6 // radians/second
      const localZ = new THREE.Vector3(0, 0, 1)
      const animate = () => {
        raf = requestAnimationFrame(animate)
        const now = performance.now()
        const dt = (now - lastT) / 1000
        lastT = now
        // Rings are scattered across link groups; traverse the root tree.
        overlayGroupRef.current?.traverse((obj) => {
          if (obj.visible && obj.userData?.kind === "axisRing") {
            obj.rotateOnAxis(localZ, SPIN_SPEED * dt)
          }
        })
        controls.update()
        renderer.render(scene, camera)
      }
      animate()

      cleanupRef.current = () => {
        cancelAnimationFrame(raf)
        ro.disconnect()
        controls.dispose()
        renderer.dispose()
        renderer.domElement.remove()
        disposables.forEach((d) => d.dispose())
        sceneRef.current = null
        ambientRef.current = null
        fillLightRef.current = null
        plateMatRef.current = null
        boltMatRef.current = null
        jointGroupsRef.current = null
      }
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err))
      setStatus("error")
    }
  // showAxes intentionally excluded — toggle handled via ref + effect below
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Toggle overlay visibility — rings/labels/triad are scattered across link
  // groups, so traverse the tree and flip any object tagged isAxisOverlay.
  useEffect(() => {
    overlayGroupRef.current?.traverse((obj) => {
      if (obj.userData?.isAxisOverlay) obj.visible = showAxes
    })
  }, [showAxes])

  // Drive each joint group's local rotation from React state.
  // Setting group.rotation[axisLetter] = θ rotates the link (and everything
  // attached downstream) around the joint's axis in real-time.
  useEffect(() => {
    const groups = jointGroupsRef.current
    if (!groups) return
    for (let i = 0; i < 6 && i < groups.length; i++) {
      const j = JOINTS[i]
      groups[i].rotation[j.axisLetter] = (angles[i] * Math.PI) / 180
    }
  }, [angles])

  // Live theme application — mutate scene materials/lights instead of rebuilding
  useEffect(() => {
    if (sceneRef.current?.background) {
      ;(sceneRef.current.background as ThreeNS.Color).setHex(palette.bg)
    }
    if (ambientRef.current) ambientRef.current.intensity = palette.ambient
    if (fillLightRef.current) {
      fillLightRef.current.color.setHex(palette.fillColor)
      fillLightRef.current.intensity = palette.fillStr
    }
    if (plateMatRef.current) {
      plateMatRef.current.color.setHex(palette.plate)
      plateMatRef.current.specular.setHex(palette.plateSpec)
    }
    if (boltMatRef.current) boltMatRef.current.color.setHex(palette.bolt)
  }, [palette])

  useEffect(() => {
    load()
    return () => cleanupRef.current?.()
  }, [load])

  // Press-and-hold ±step helper.  Single click nudges by STEP_DEG, but holding
  // the button down repeats every HOLD_INTERVAL_MS so the user can drive an
  // axis through its full range smoothly.
  const STEP_DEG = 3
  const HOLD_INTERVAL_MS = 60
  const holdTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const stepAxis = useCallback((idx: number, delta: number) => {
    setAngles((prev) => {
      const next = [...prev]
      const j = JOINTS[idx]
      next[idx] = Math.max(j.min, Math.min(j.max, prev[idx] + delta))
      return next
    })
  }, [])

  const startHold = useCallback((idx: number, dir: 1 | -1) => {
    stepAxis(idx, dir * STEP_DEG)
    if (holdTimerRef.current) clearInterval(holdTimerRef.current)
    holdTimerRef.current = setInterval(
      () => stepAxis(idx, dir * STEP_DEG),
      HOLD_INTERVAL_MS
    )
  }, [stepAxis])

  const stopHold = useCallback(() => {
    if (holdTimerRef.current) {
      clearInterval(holdTimerRef.current)
      holdTimerRef.current = null
    }
  }, [])

  const resetAngles = useCallback(() => {
    setAngles([0, 0, 0, 0, 0, 0])
  }, [])

  // Cleanup any active hold interval on unmount
  useEffect(() => () => stopHold(), [stopHold])

  return (
    <div className="flex flex-col h-full rounded-xl border border-border bg-card overflow-hidden ring-1 ring-foreground/5">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border/40 bg-muted/30 shrink-0">
        <Box className="size-3.5 text-[#005AAB] shrink-0" />
        <span className="text-xs font-semibold tracking-wide text-foreground truncate">
          KR10 R1100 sixx — 3D CAD
        </span>
        <button
          onClick={() => setShowAxes((v) => !v)}
          className="ml-auto flex items-center gap-1 text-[10px] font-mono text-muted-foreground hover:text-foreground transition-colors"
          title={showAxes ? "Hide axes & joints" : "Show axes & joints"}
        >
          {showAxes ? <Eye className="size-3" /> : <EyeOff className="size-3" />}
          Axes
        </button>
        <span className="text-[10px] font-mono text-muted-foreground shrink-0">
          KUKA / KRC4
        </span>
      </div>

      {/* Viewer area — fills available height; theme palette drives bg */}
      <div className="relative flex-1 min-h-[200px] bg-[#eef2f6] dark:bg-[#0d1117] transition-colors">
        {status === "loading" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 z-10">
            <div className="size-8 rounded-full border-2 border-[#005AAB]/30 border-t-[#005AAB] animate-spin" />
            <span className="text-xs text-muted-foreground">Parsing STEP file…</span>
            <span className="text-[10px] text-muted-foreground/50">
              First load may take 15–30 s
            </span>
          </div>
        )}

        {status === "error" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 z-10 px-4">
            <AlertCircle className="size-7 text-red-400/60" />
            <span className="text-xs text-muted-foreground text-center break-all">
              {errorMsg}
            </span>
            <button
              onClick={load}
              className="flex items-center gap-1 mt-1 text-xs text-muted-foreground hover:text-foreground underline transition-colors"
            >
              <RotateCcw className="size-3" />
              Retry
            </button>
          </div>
        )}

        {/* Three.js canvas mount point */}
        <div
          ref={mountRef}
          className={`absolute inset-0 ${status === "ready" ? "" : "invisible"}`}
          style={{ touchAction: "none" }}
        />

        {/* Joint control panel — bottom strip with A1..A6 -/+ steppers */}
        {status === "ready" && showControls && (
          <div className="absolute bottom-2 left-2 right-14 z-10 grid grid-cols-3 sm:grid-cols-6 gap-1.5 p-1.5 rounded-lg bg-white/85 dark:bg-black/65 backdrop-blur-sm border border-black/10 dark:border-white/10 shadow-lg">
            {JOINTS.map((j, idx) => {
              const v = angles[idx]
              const atMin = v <= j.min + 0.001
              const atMax = v >= j.max - 0.001
              return (
                <div
                  key={j.id}
                  className="flex flex-col items-center gap-0.5 px-1 py-0.5 rounded-md bg-muted/30"
                >
                  <div className="flex items-center gap-1 w-full">
                    <span
                      className="size-1.5 rounded-full shrink-0"
                      style={{ background: `#${j.hue.toString(16).padStart(6, "0")}` }}
                    />
                    <span className="text-[10px] font-mono font-bold text-foreground">
                      {j.id}
                    </span>
                    <span className="ml-auto text-[9px] font-mono tabular-nums text-muted-foreground">
                      {v.toFixed(0)}°
                    </span>
                  </div>
                  <div className="flex items-center gap-1 w-full">
                    <button
                      type="button"
                      onPointerDown={() => startHold(idx, -1)}
                      onPointerUp={stopHold}
                      onPointerLeave={stopHold}
                      onPointerCancel={stopHold}
                      disabled={atMin}
                      className="flex-1 h-5 rounded flex items-center justify-center bg-muted hover:bg-accent active:bg-accent/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      title={`Rotate ${j.id} negative (min ${j.min}°)`}
                    >
                      <Minus className="size-3" />
                    </button>
                    <button
                      type="button"
                      onPointerDown={() => startHold(idx, +1)}
                      onPointerUp={stopHold}
                      onPointerLeave={stopHold}
                      onPointerCancel={stopHold}
                      disabled={atMax}
                      className="flex-1 h-5 rounded flex items-center justify-center bg-muted hover:bg-accent active:bg-accent/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      title={`Rotate ${j.id} positive (max ${j.max}°)`}
                    >
                      <Plus className="size-3" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Bottom-right floating button: hide/show the control panel.
            Doubles as a "reset to home" shortcut via long-press (right-click). */}
        {status === "ready" && (
          <button
            type="button"
            onClick={() => setShowControls((v) => !v)}
            onContextMenu={(e) => { e.preventDefault(); resetAngles() }}
            className="absolute bottom-2 right-2 z-20 size-10 rounded-full flex items-center justify-center bg-white/90 dark:bg-black/70 hover:bg-white dark:hover:bg-black/90 backdrop-blur-sm border border-black/10 dark:border-white/15 shadow-lg transition-colors"
            title={
              showControls
                ? "Hide joint controls (right-click → reset all to 0°)"
                : "Show joint controls (right-click → reset all to 0°)"
            }
          >
            {showControls ? (
              <ChevronDown className="size-4" />
            ) : (
              <SlidersHorizontal className="size-4" />
            )}
          </button>
        )}
      </div>
    </div>
  )
}
